import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, MessageSquareQuote, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";
import { TrustBadge } from "@/components/TrustBadge";
import { PostCard, timeAgo } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";

export default function PublicProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [vouching, setVouching] = useState(false);

  useEffect(() => { api.get(`/users/${id}`).then((r) => setData(r.data)).catch(() => navigate("/app")); }, [id, navigate]);

  const vouch = async () => {
    setVouching(true);
    try {
      const r = await api.post(`/users/${id}/vouch`);
      toast.success("Thank you for vouching for them!");
      setData((d) => ({ ...d, user: { ...d.user, vouch_count: r.data.vouch_count } }));
    } catch (e) { toast.error(apiError(e)); } finally { setVouching(false); }
  };

  if (!data) return <div className="h-64 rounded-2xl bg-white/60 border-2 border-border animate-pulse" />;
  const { user, reviews, posts } = data;
  const isSelf = me?.user_id === user.user_id;

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-forest-700 font-semibold mb-4" data-testid="back-btn">
        <ArrowLeft size={20} strokeWidth={2.5} /> Back
      </button>

      <div className="bg-white rounded-2xl border-2 border-border shadow-sm p-6 sm:p-8">
        <div className="flex items-center gap-5">
          <Avatar user={user} size={96} />
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-forest-900">{user.name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-1"><StarRating value={user.rating_avg} count={user.rating_count} size={18} /><TrustBadge level={user.trust_level} size="sm" /></div>
            {user.address && <p className="flex items-center gap-1.5 text-forest-700 mt-1"><MapPin size={16} strokeWidth={2.5} className="text-terracotta" />{user.address}</p>}
          </div>
        </div>
        {user.bio && <p className="text-lg text-forest-700 leading-relaxed mt-5">{user.bio}</p>}
        {user.interests?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {user.interests.map((it) => <span key={it} className="px-4 py-2 rounded-full bg-cream border-2 border-border text-forest-700 font-semibold">{it}</span>)}
          </div>
        )}
        {!isSelf && (
          <div className="mt-5 pt-5 border-t border-border flex items-center gap-3 flex-wrap">
            <span className="text-forest-700 font-medium">{user.vouch_count || 0} member{user.vouch_count === 1 ? "" : "s"} vouch for {user.name.split(" ")[0]}</span>
            <button onClick={vouch} disabled={vouching} data-testid="vouch-btn"
              className="ml-auto min-h-[48px] px-5 rounded-full bg-forest-700 text-white font-semibold hover:bg-forest-900 transition-colors flex items-center gap-2 disabled:opacity-60">
              <ShieldCheck size={18} strokeWidth={2.5} /> I trust this person
            </button>
          </div>
        )}
      </div>

      {posts?.length > 0 && (
        <div className="mt-6">
          <h2 className="font-head text-2xl font-bold text-forest-900 mb-4">{user.name.split(" ")[0]}'s posts</h2>
          <div className="grid gap-4 sm:grid-cols-2">{posts.map((p) => <PostCard key={p.post_id} post={p} />)}</div>
        </div>
      )}

      <div className="mt-6">
        <h2 className="font-head text-2xl font-bold text-forest-900 mb-4 flex items-center gap-2">
          <MessageSquareQuote size={26} strokeWidth={2.5} className="text-terracotta" /> Reviews
        </h2>
        {reviews.length === 0 ? (
          <p className="text-forest-700 bg-white rounded-2xl border-2 border-border p-6">No reviews yet.</p>
        ) : (
          <div className="space-y-3" data-testid="reviews-list">
            {reviews.map((r) => (
              <div key={r.review_id} className="bg-white rounded-2xl border-2 border-border p-5">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-forest-900">{r.reviewer_name}</span>
                  <span className="text-forest-500 text-sm ml-auto">{timeAgo(r.created_at)}</span>
                </div>
                <div className="my-1.5"><StarRating value={r.rating} size={16} /></div>
                {r.comment && <p className="text-forest-700 leading-relaxed">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
